// Is the signed-in account in the beta? Settings shows its Beta row only on a clear yes.
//
// It asks the same GET /account/beta the /beta page asks (nginx hands /account/beta to the beta
// controller, which answers {available:true, ...} or {available:false} for the account behind the
// token and nothing else). Where that route isn't wired up, the request lands on the normal server
// and gets a 404, which reads as no. Signed out, nothing is asked at all.
//
// Answers are kept in this tab for a few minutes, tied to the account, so searching Settings
// doesn't ask the server on every keystroke. Only true or false is kept, never the answer itself.

const SESSION_KEY = "revault:account";
const CACHE_KEY = "rv:beta-access";
const KEEP_MS = 5 * 60 * 1000;
const KEEP_FAIL_MS = 60 * 1000;
const WAIT_MS = 5000;

let memo = null; // { who, member, until }
let pending = null; // { who, promise }

function session() {
	try {
		const j = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
		return j && typeof j.token === "string" && j.token ? j : null;
	} catch (e) { return null; }
}
// Which account an answer belongs to. The account id when we have it, so a renewed token keeps
// the answer; otherwise the token itself.
function whoOf(s) { return s ? String((s.profile && s.profile.sub) || "t:" + s.token) : ""; }

function load() {
	if (memo) return memo;
	try {
		const j = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
		if (j && typeof j.who === "string" && typeof j.member === "boolean" && typeof j.until === "number") memo = j;
	} catch (e) { /* storage off */ }
	return memo;
}
function save(who, member, keep) {
	memo = { who, member, until: Date.now() + keep };
	try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(memo)); } catch (e) { /* storage off */ }
}

// true or false from the last answer for this account, or null when there's none (or it's old).
export function cachedBetaMember() {
	const s = session();
	if (!s) return false;
	const m = load();
	return m && m.who === whoOf(s) && m.until > Date.now() ? m.member : null;
}

// Asks the server unless a recent answer for this account is kept. Never throws; anything but a
// clear yes is false.
export function checkBetaMember(opts = {}) {
	const s = session();
	if (!s) return Promise.resolve(false);
	const who = whoOf(s);
	if (!opts.fresh) {
		const c = cachedBetaMember();
		if (c !== null) return Promise.resolve(c);
	}
	if (pending && pending.who === who) return pending.promise;
	const promise = (async () => {
		const ctl = typeof AbortController === "function" ? new AbortController() : null;
		const timer = ctl ? setTimeout(() => ctl.abort(), WAIT_MS) : 0;
		try {
			const r = await fetch("/account/beta", {
				headers: { Authorization: "Bearer " + s.token },
				cache: "no-store",
				credentials: "same-origin",
				signal: ctl ? ctl.signal : undefined,
			});
			let member = false;
			if (r.ok && /json/.test(r.headers.get("content-type") || "")) {
				const d = await r.json().catch(() => null);
				member = !!(d && d.available === true);
			}
			// a different account may have signed in while we waited
			if (whoOf(session()) === who) save(who, member, KEEP_MS);
			return member;
		} catch (e) {
			if (whoOf(session()) === who) save(who, false, KEEP_FAIL_MS);
			return false;
		} finally {
			if (timer) clearTimeout(timer);
			if (pending && pending.who === who) pending = null;
		}
	})();
	pending = { who, promise };
	return promise;
}

// The gated Settings rows this account can see right now, for search. Uses what's kept and asks
// in the background when nothing is, so a member finds the row on their next search.
export function betaGates() {
	const c = cachedBetaMember();
	if (c === null) void checkBetaMember();
	return c === true ? new Set(["beta"]) : new Set();
}

// Forget the kept answer (sign out, account switch).
export function forgetBetaMember() {
	memo = null;
	pending = null;
	try { sessionStorage.removeItem(CACHE_KEY); } catch (e) { /* storage off */ }
}
